/**
 * GreenSyn Inventory — capability model.
 *
 * Roles are *data*; capabilities are the code. Two kinds of role exist:
 *
 *  - **System roles** (`SYSTEM_ROLES`, below) — admin, warehouse, production,
 *    qc, viewer. They cannot be renamed or re-scoped, because the system depends
 *    on them existing: `admin` in particular is the guaranteed way back in, and
 *    losing it would lock everyone out with no fix that does not involve psql.
 *  - **Custom roles** — rows in the `roles` table that an administrator creates
 *    by picking a subset of `CAPABILITIES`. They can be edited and deleted.
 *
 * The closed set below is the point: a role can only ever hold capabilities the
 * code actually checks. That is enforced in three places — this type, a zod enum
 * at the edge, and a database CHECK constraint — so a typo cannot invent a
 * permission that nothing enforces.
 *
 * A role declares two independent things:
 *
 *  - **`capabilities`** — what its holder may *do*. The API enforces this, and
 *    the web app hides what it cannot do.
 *  - **`sections`** — which screens its *workspace* presents (see `SECTIONS`).
 *    Purely presentation. Nothing is secured by it; it decides what the sidebar
 *    shows while that role's view is selected.
 *
 * Keeping those apart is what lets a "read what production is doing" role exist
 * without also handing it the power to run batches, and vice versa.
 *
 * A user may hold several roles. Their permissions are the **union** — every
 * capability of every role they hold, so the role picker can never take away an
 * action they are entitled to — while the *view* is one role's workspace at a
 * time, which is what "tailored UI" means here. `can(...)` is therefore asked
 * about capabilities and never about a role.
 */

export const CAPABILITIES = [
  /** Create/edit items, locations, suppliers, recipes. */
  'masterdata.manage',
  /** Receive, issue, transfer, adjust, scrap stock. */
  'inventory.manage',
  /** Create/start/complete/close production batches and record consumption. */
  'batch.manage',
  /** Draw outside recipe tolerance, or add ad-hoc/substitute lines. */
  'batch.override',
  /** Record material recovered from a batch. */
  'recovery.record',
  /** Override the item's recovered-material release policy for one recovery. */
  'recovery.overridePolicy',
  /** Pass or fail a quality check, releasing quarantined material. */
  'qc.release',
  /** Reverse a closed batch. */
  'batch.reopen',
  /** Manage users and roles. */
  'users.manage',
  /** Read the audit trail. */
  'audit.read',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

/**
 * The catalogue an administrator picks from. Kept beside the capability list so
 * a new capability cannot be added to the code without also being described
 * here — the type makes the omission a compile error.
 */
export interface CapabilityMeta {
  code: Capability;
  /** What the administrator sees in the role editor. */
  label: string;
  /** Groups related capabilities in the editor. */
  group: string;
  description: string;
}

export const CAPABILITY_GROUPS = [
  'Master data',
  'Inventory',
  'Production',
  'Quality',
  'Administration',
] as const;

export const CAPABILITY_META: readonly CapabilityMeta[] = [
  {
    code: 'masterdata.manage',
    label: 'Manage master data',
    group: 'Master data',
    description: 'Create and edit items, locations, suppliers and recipes.',
  },
  {
    code: 'inventory.manage',
    label: 'Move stock',
    group: 'Inventory',
    description: 'Receive, issue, transfer, adjust and scrap stock, and correct a movement.',
  },
  {
    code: 'batch.manage',
    label: 'Run production batches',
    group: 'Production',
    description: 'Create, start, complete, close or cancel a batch and record what it draws.',
  },
  {
    code: 'batch.override',
    label: 'Override batch tolerance',
    group: 'Production',
    description:
      'Draw more than the recipe tolerance allows, or add ad-hoc and substitute lines. Every override needs a reason.',
  },
  {
    code: 'recovery.record',
    label: 'Record recovered material',
    group: 'Production',
    description: 'Record material recovered from a batch or from stock, putting it back into inventory.',
  },
  {
    code: 'recovery.overridePolicy',
    label: 'Override recovery release policy',
    group: 'Quality',
    description:
      "Release recovered material immediately when the item's policy would quarantine it. Needs a reason.",
  },
  {
    code: 'qc.release',
    label: 'Release or reject material',
    group: 'Quality',
    description:
      'Pass or fail a quality check, which releases quarantined material or rejects it. Rejecting is a one-way door.',
  },
  {
    code: 'batch.reopen',
    label: 'Reopen a closed batch',
    group: 'Production',
    description: 'Reverse a closed batch and its output.',
  },
  {
    code: 'users.manage',
    label: 'Manage users and roles',
    group: 'Administration',
    description: 'Create and deactivate users, change who holds which role, and define custom roles.',
  },
  {
    code: 'audit.read',
    label: 'Read the audit trail',
    group: 'Administration',
    description: 'See who changed what, when, and what it looked like beforehand.',
  },
];

/* -------------------------------------------------------------------------- */
/* Sections — what a role's workspace shows                                    */
/* -------------------------------------------------------------------------- */

/**
 * The screens a role's view can present. Closed for the same reason capabilities
 * are: a section the web app does not have would be a sidebar entry that goes
 * nowhere. The database carries a matching CHECK.
 */
export const SECTIONS = [
  'dashboard',
  'items',
  'locations',
  'suppliers',
  'inventory',
  'recipes',
  'batches',
  'recovery',
  'quality',
  'reports',
  'audit',
  'users',
  'roles',
] as const;

export type Section = (typeof SECTIONS)[number];

export interface SectionMeta {
  code: Section;
  label: string;
  /** Groups related sections in the role editor. */
  group: string;
}

export const SECTION_GROUPS = [
  'Overview',
  'Master data',
  'Operations',
  'Insight',
  'Administration',
] as const;

export const SECTION_META: readonly SectionMeta[] = [
  { code: 'dashboard', label: 'Dashboard', group: 'Overview' },
  { code: 'items', label: 'Items', group: 'Master data' },
  { code: 'locations', label: 'Locations', group: 'Master data' },
  { code: 'suppliers', label: 'Suppliers', group: 'Master data' },
  { code: 'inventory', label: 'Inventory', group: 'Operations' },
  { code: 'recipes', label: 'Recipes', group: 'Operations' },
  { code: 'batches', label: 'Production batches', group: 'Operations' },
  { code: 'recovery', label: 'Recovery', group: 'Operations' },
  { code: 'quality', label: 'Quality control', group: 'Operations' },
  { code: 'reports', label: 'Reports', group: 'Insight' },
  { code: 'audit', label: 'Audit trail', group: 'Insight' },
  { code: 'users', label: 'Users', group: 'Administration' },
  { code: 'roles', label: 'Roles', group: 'Administration' },
];

/**
 * The screens that are useful to every role and read-only to reach: stock on
 * hand, what an item is, where it sits, and the reports over it. Every workspace
 * starts from these.
 */
export const CORE_SECTIONS: readonly Section[] = [
  'dashboard',
  'items',
  'locations',
  'suppliers',
  'inventory',
  'reports',
];

/** The screens a capability makes relevant. Unlisted capabilities imply none. */
const SECTIONS_BY_CAPABILITY: Partial<Record<Capability, readonly Section[]>> = {
  'masterdata.manage': ['recipes'],
  'batch.manage': ['recipes', 'batches'],
  'recovery.record': ['recovery'],
  'qc.release': ['quality'],
  'users.manage': ['users', 'roles'],
  'audit.read': ['audit'],
};

/**
 * The workspace suggested for a role with these capabilities — used to pre-fill
 * the role editor and as the API default when a new role omits `sections`.
 *
 * A suggestion, not a rule: the whole reason sections are stored rather than
 * derived is that an administrator may want a role that *may* run batches while
 * its workspace shows only the stock it consumes.
 */
export function defaultSectionsFor(capabilities: readonly Capability[]): Section[] {
  const wanted = new Set<Section>(CORE_SECTIONS);
  for (const capability of capabilities) {
    for (const section of SECTIONS_BY_CAPABILITY[capability] ?? []) wanted.add(section);
  }
  // Emitted in canonical order so two equal sets are byte-identical.
  return SECTIONS.filter((section) => wanted.has(section));
}

/* -------------------------------------------------------------------------- */
/* Roles                                                                       */
/* -------------------------------------------------------------------------- */

/** A role as it is stored: a named set of capabilities, plus a workspace. */
export interface RoleDefinition {
  code: string;
  name: string;
  description: string;
  /** System roles cannot be renamed or re-scoped. Their `sections` stay tunable. */
  isSystem: boolean;
  capabilities: readonly Capability[];
  sections: readonly Section[];
}

/**
 * The built-in roles. The values here are the canonical definition: the database
 * rows are created by a migration, and an integration test asserts the two still
 * agree, so this cannot quietly drift from what is actually in the database.
 *
 * `admin` deliberately holds every capability and cannot be re-scoped. That is
 * what makes "you cannot lock yourself out of role management" a structural fact
 * rather than a promise: there is always at least one role that can manage users.
 */
export const SYSTEM_ROLES: readonly RoleDefinition[] = [
  {
    code: 'admin',
    name: 'Administrator',
    description: 'Full access, including user management and the audit trail.',
    isSystem: true,
    capabilities: CAPABILITIES,
    sections: SECTIONS,
  },
  {
    code: 'warehouse',
    name: 'Warehouse',
    description: 'Moves stock: receipts, issues, transfers, adjustments, scrap.',
    isSystem: true,
    capabilities: ['inventory.manage'],
    sections: ['dashboard', 'items', 'locations', 'suppliers', 'inventory', 'reports'],
  },
  {
    code: 'production',
    name: 'Production',
    description: 'Runs batches and records recovered material.',
    isSystem: true,
    capabilities: ['batch.manage', 'batch.override', 'recovery.record'],
    sections: [
      'dashboard',
      'items',
      'locations',
      'suppliers',
      'inventory',
      'recipes',
      'batches',
      'recovery',
      'reports',
    ],
  },
  {
    code: 'qc',
    name: 'Quality Control',
    description: 'Releases or rejects quarantined material.',
    isSystem: true,
    capabilities: ['qc.release', 'recovery.overridePolicy'],
    sections: ['dashboard', 'items', 'locations', 'suppliers', 'inventory', 'recipes', 'quality', 'reports'],
  },
  {
    code: 'viewer',
    name: 'Viewer',
    description: 'Read-only access to everything.',
    isSystem: true,
    capabilities: [],
    // Every reading screen: this role exists to look, so its workspace is the
    // whole read-only application rather than the handful of core screens.
    sections: [
      'dashboard',
      'items',
      'locations',
      'suppliers',
      'inventory',
      'recipes',
      'batches',
      'recovery',
      'quality',
      'reports',
    ],
  },
];

export const SYSTEM_ROLE_CODES: readonly string[] = SYSTEM_ROLES.map((role) => role.code);

/**
 * Custom role codes end up in URLs, log lines and CSVs, so they are restricted
 * to something that needs no escaping anywhere. The matching database CHECK
 * enforces the same pattern.
 */
export const ROLE_CODE_PATTERN = /^[a-z][a-z0-9_]{1,39}$/;

/** Whether a set of capabilities permits an action. */
export function can(capabilities: readonly Capability[], capability: Capability): boolean {
  return capabilities.includes(capability);
}

/**
 * The union of several roles' capabilities: what a user who holds all of them
 * may do. Order follows `CAPABILITIES` so the result is stable rather than
 * dependent on the order roles were assigned in.
 */
export function unionCapabilities(
  roles: readonly { capabilities: readonly Capability[] }[],
): Capability[] {
  const held = new Set<Capability>();
  for (const role of roles) for (const capability of role.capabilities) held.add(capability);
  return CAPABILITIES.filter((capability) => held.has(capability));
}

/**
 * Which role's workspace to open by default: the one that can do the most, so
 * nobody signs in unable to find their work. Ties break on the role code, so the
 * choice is deterministic rather than depending on query order.
 *
 * The user can switch to any role they hold at any time — this only picks the
 * starting view.
 */
export function mostCapableRole<T extends { code: string; capabilities: readonly Capability[] }>(
  roles: readonly T[],
): T | null {
  const ranked = [...roles].sort(
    (a, b) => b.capabilities.length - a.capabilities.length || a.code.localeCompare(b.code),
  );
  return ranked[0] ?? null;
}
